import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MessageCircle, Send, ArrowLeft, ShieldAlert, Phone, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 8000;

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const formatRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d) / 60000);
    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `${diffMin} min`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)} h`;
    if (diffMin < 10080) return `${Math.floor(diffMin / 1440)} j`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch { return ""; }
};

/**
 * Generic messages page — works for both patient and practitioner.
 * The current JWT is automatically used via axios.defaults.headers.
 *
 * @param basePath: "/messages" or "/pro/messages"
 * @param currentUserId: ID of the logged-in user (to mark own bubbles)
 */
export default function MessagesPage({ basePath, currentUserId }) {
  const { otherUserId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [eligible, setEligible] = useState(true);
  const [consentNeeded, setConsentNeeded] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const bottomRef = useRef(null);
  const isPatient = !basePath.startsWith("/practitioner");

  // GDPR chat consent — patients only (practitioners are bound by professional secret)
  useEffect(() => {
    if (!isPatient) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/me/chat-consent`);
        if (!cancelled && !res.data?.accepted) setConsentNeeded(true);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [isPatient]);

  const acceptChatConsent = async () => {
    setConsentLoading(true);
    try {
      await axios.post(`${API_URL}/me/chat-consent`, {});
      setConsentNeeded(false);
    } catch (e) {
      alert("Impossible d'enregistrer votre acceptation. Réessayez.");
    } finally {
      setConsentLoading(false);
    }
  };

  // Load conversations
  const loadConvs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/messages/conversations`);
      setConversations(res.data || []);
    } catch {}
    setLoadingConvs(false);
  }, []);

  useEffect(() => {
    loadConvs();
    const i = setInterval(loadConvs, POLL_MS);
    return () => clearInterval(i);
  }, [loadConvs]);

  // Load thread when otherUserId changes
  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/messages/conversations/${id}`);
      setThread(res.data || []);
      await axios.patch(`${API_URL}/messages/conversations/${id}/read`).catch(() => {});
      try {
        const el = await axios.get(`${API_URL}/messages/eligibility/${id}`);
        setEligible(!!el.data?.eligible);
      } catch { setEligible(true); }
      const found = conversations.find((c) => c.other_user_id === id);
      if (found) setActiveConv(found);
    } catch {}
    setLoadingThread(false);
  }, [conversations]);

  useEffect(() => {
    if (!otherUserId) {
      setActiveConv(null);
      setThread([]);
      return;
    }
    setLoadingThread(true);
    loadThread(otherUserId);
    const i = setInterval(() => loadThread(otherUserId), POLL_MS);
    return () => clearInterval(i);
  }, [otherUserId, loadThread]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const send = async () => {
    const content = input.trim();
    if (!content || !otherUserId) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/messages`, {
        recipient_id: otherUserId,
        content,
      });
      setThread((prev) => [...prev, res.data]);
      setInput("");
      loadConvs();
    } catch (e) {
      alert(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* First-time chat consent modal (patients only) */}
      {consentNeeded && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="chat-consent-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
            <div className="w-14 h-14 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <ShieldAlert className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
              Avant de commencer la conversation
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              La messagerie DoctoDays est un <strong>canal logistique</strong> destiné à
              coordonner votre rendez-vous (horaires, retard, accès, documents administratifs).
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Pour discuter de votre <strong>état de santé</strong>, contactez le cabinet par téléphone ou prenez RDV.</span>
              </li>
              <li className="flex gap-2 text-sm text-slate-700">
                <Phone className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>En cas d'urgence vitale, composez le <strong>15 (SAMU)</strong> ou le <strong>112</strong>.</span>
              </li>
            </ul>
            <p className="text-xs text-slate-500 mb-5">
              En cliquant "J'ai compris", vous reconnaissez que cet espace n'est pas adapté
              au partage d'informations médicales sensibles. Tout message contenant un terme
              clinique sera automatiquement bloqué.
            </p>
            <button
              onClick={acceptChatConsent}
              disabled={consentLoading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-3 rounded-xl font-semibold"
              data-testid="chat-consent-accept"
            >
              {consentLoading ? "Enregistrement…" : "J'ai compris"}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full mt-2 text-slate-500 text-sm hover:underline"
              data-testid="chat-consent-cancel"
            >
              Annuler et retourner
            </button>
          </div>
        </div>
      )}

      <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6 flex items-center gap-2">
        <MessageCircle className="w-7 h-7 text-emerald-600" />
        Messages
      </h1>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden grid grid-cols-1 md:grid-cols-3 min-h-[60vh]">
        {/* Conversations list */}
        <aside className={`border-r border-slate-200 md:col-span-1 ${otherUserId ? "hidden md:block" : ""}`}>
          <div className="p-4 border-b border-slate-100 font-semibold text-slate-700">
            Conversations
          </div>
          {loadingConvs ? (
            <div className="p-6 text-slate-400 text-sm">Chargement...</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 space-y-3">
              <p>Aucune conversation pour le moment.</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                {basePath.startsWith("/practitioner")
                  ? "Pour démarrer une conversation, ouvrez un rendez-vous depuis votre tableau de bord et cliquez sur « Envoyer un message au patient »."
                  : "Pour démarrer une conversation avec un praticien, allez sur « Mes rendez-vous » et cliquez sur « Message » à côté de votre RDV à venir."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {conversations.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`${basePath}/${c.other_user_id}`}
                    className={`flex items-center gap-3 p-4 hover:bg-emerald-50 transition ${
                      otherUserId === c.other_user_id ? "bg-emerald-50" : ""
                    }`}
                    data-testid={`web-conv-${c.other_user_id}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center shrink-0">
                      {(c.other_user_name || "?").split(" ").map(p => p[0] || "").slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-800 truncate">{c.other_user_name}</span>
                        {c.last_message_at && (
                          <span className="text-xs text-slate-400 shrink-0 ml-2">
                            {formatRelative(c.last_message_at)}
                          </span>
                        )}
                      </div>
                      {c.other_user_specialty && (
                        <div className="text-xs text-emerald-600 truncate">{c.other_user_specialty}</div>
                      )}
                      <div className="text-sm text-slate-500 truncate">{c.last_message}</div>
                    </div>
                    {c.unread_count > 0 && (
                      <span className="min-w-5 h-5 px-1.5 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {c.unread_count}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Thread */}
        <section className={`md:col-span-2 flex flex-col ${!otherUserId ? "hidden md:flex" : "flex"}`}>
          {!otherUserId ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              Sélectionnez une conversation
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                <button
                  onClick={() => navigate(basePath)}
                  className="md:hidden p-2 rounded-full hover:bg-slate-100 text-slate-500"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <div className="font-semibold text-slate-800">
                    {activeConv?.other_user_name || "Conversation"}
                  </div>
                  {activeConv?.other_user_specialty && (
                    <div className="text-xs text-emerald-600">{activeConv.other_user_specialty}</div>
                  )}
                </div>
              </div>

              {/* Persistent GDPR banner — chat is logistics only */}
              <div
                className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs flex items-center gap-2"
                data-testid="chat-banner-logistics"
              >
                <ShieldAlert className="w-4 h-4 shrink-0 text-amber-600" />
                <span className="leading-snug">
                  Cet espace est dédié à la <strong>coordination logistique</strong> du
                  rendez-vous (horaires, accès, présence). <strong>Pas d'informations médicales.</strong>{" "}
                  En cas d'urgence, contactez votre praticien par téléphone ou le 15.
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" style={{ minHeight: 300, maxHeight: "60vh" }}>
                {loadingThread ? (
                  <div className="text-center text-slate-400 py-10">Chargement...</div>
                ) : thread.length === 0 ? (
                  <div className="text-center text-slate-400 py-10">
                    Aucun message. Envoyez-en un pour commencer.
                  </div>
                ) : (
                  thread.map((m) => {
                    const mine = m.sender_id === currentUserId;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                            mine
                              ? "bg-emerald-600 text-white rounded-tr-sm"
                              : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"
                          }`}
                        >
                          <div className="whitespace-pre-wrap break-words text-sm">{m.content}</div>
                          <div className={`text-[10px] mt-1 text-right ${mine ? "text-emerald-100" : "text-slate-400"}`}>
                            {formatTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {!eligible ? (
                <div className="p-3 bg-red-50 border-t border-red-200 text-red-700 text-sm text-center">
                  Vous ne pouvez plus écrire — aucun RDV à venir et aucun RDV dans les 30 derniers jours.
                </div>
              ) : (
                <div className="p-3 border-t border-slate-200 bg-white flex gap-2 items-end">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Écrire un message..."
                    rows={1}
                    className="flex-1 resize-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    maxLength={4000}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    data-testid="web-chat-input"
                  />
                  <button
                    onClick={send}
                    disabled={!input.trim() || sending}
                    className="w-11 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center justify-center shrink-0"
                    data-testid="web-chat-send"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
